import {
  buildInstrumentExport,
  type ExportableInstrument,
  type InstrumentExportFile,
} from "../instruments/instrumentExport";
import { EXTERNAL_CONFIGURATOR_BASE_OPTIONS } from "./externalConfiguratorBaseOptions";
import {
  answersToName,
  buildPresetExport,
  CREEPER_ROLLER_TYPE,
  type ExportablePreset,
  type ExportedPreset,
  PART_SELECTION_KINDS_BY_CATEGORY,
  PresetExportError,
  type PresetExportOption,
  type PresetExportProblem,
} from "./presetExport";
import { exportAllOptions } from "./userOptionsExport";

/** What can keep the external configurator's files from being written. */
export enum ExternalConfiguratorExportProblemCode {
  /** No option answers to a name the configurator always asks for. */
  BaseOptionMissing = "baseOptionMissing",
  /** Options answer to such a name, but none is offered externally. */
  BaseOptionNotAvailable = "baseOptionNotAvailable",
  /** An offered preset uses an option that is not offered externally. */
  PresetOptionNotAvailable = "presetOptionNotAvailable",
  /** An offered preset names an option id that no offered option has. */
  PresetOptionUnknown = "presetOptionUnknown",
  /** An offered preset cannot be written at all (`buildPresetExport`). */
  PresetNotExportable = "presetNotExportable",
  /** An offered option has a German description but no English one. */
  OptionDescriptionEnMissing = "optionDescriptionEnMissing",
  /** An offered preset has no English name. */
  PresetNameEnMissing = "presetNameEnMissing",
}

/**
 * One problem of the external export. Which fields are set depends on the
 * code; the frontend writes the sentence.
 */
export interface ExternalConfiguratorExportProblem {
  code: ExternalConfiguratorExportProblemCode;
  /** The kind and name the configurator asks for (base options). */
  kind?: string;
  name?: string;
  /** The option concerned, where there is one. */
  optionId?: string;
  optionName?: string;
  optionKind?: string;
  /** The preset concerned and its reference field. */
  presetId?: string;
  presetName?: string;
  field?: string;
  /** `PresetNotExportable`: the reason as `buildPresetExport` gives it. */
  reason?: PresetExportProblem;
}

/** A user option as the list atom carries it. */
export interface ExternalExportOption extends PresetExportOption {
  availableExternally?: boolean;
  description?: unknown;
  descriptionEn?: unknown;
}

/**
 * The files the external configurator reads. `userOptions` and `presets` are
 * written together or not at all, so the pair always matches; `instruments`
 * does not depend on them and is always built.
 */
export interface ExternalConfiguratorExport {
  problems: ExternalConfiguratorExportProblem[];
  /** `user-options.json`; `null` when there are problems. */
  userOptions: Record<string, unknown>[] | null;
  /** `presets.json`; `null` when there are problems. */
  presets: ExportedPreset[] | null;
  /** `instruments.json`. */
  instruments: InstrumentExportFile;
  /** Instruments left out because their id is not a uuid. */
  skippedInstrumentIds: string[];
}

export interface ExternalConfiguratorExportInput {
  options: readonly ExternalExportOption[];
  presets: readonly ExportablePreset[];
  instruments: readonly ExportableInstrument[];
  now?: Date;
}

const ROLLER_KIND = "RollerType";
const PADDING_KIND = "PaddingType";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isOffered = (option: ExternalExportOption): boolean =>
  option.availableExternally === true && option.deleted !== true;

const trimmedText = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

/**
 * One option as the configurator reads it. The flag only steers this export,
 * and the German description never leaves the app: `description` is the
 * trimmed English text, and the key is left out when there is none, because
 * the configurator falls back to the name only for a missing key (`??`).
 */
const toExternalOption = (
  option: Record<string, unknown>,
): Record<string, unknown> => {
  const {
    availableExternally: _flag,
    descriptionEn,
    description: _german,
    ...rest
  } = option;
  const english = trimmedText(descriptionEn);
  return english ? { ...rest, description: english } : rest;
};

/** Offered options whose German description has no English counterpart. */
function checkDescriptionsEn(
  offered: readonly ExternalExportOption[],
): ExternalConfiguratorExportProblem[] {
  return offered
    .filter(
      (option) =>
        trimmedText(option.description) !== "" &&
        trimmedText(option.descriptionEn) === "",
    )
    .map((option) => ({
      code: ExternalConfiguratorExportProblemCode.OptionDescriptionEnMissing,
      optionId: option.id,
      optionName: option.name,
      optionKind: option.kind,
    }));
}

function checkBaseOptions(
  options: readonly ExternalExportOption[],
): ExternalConfiguratorExportProblem[] {
  const problems: ExternalConfiguratorExportProblem[] = [];
  for (const { kind, name } of EXTERNAL_CONFIGURATOR_BASE_OPTIONS) {
    const answering = options.filter(
      (option) =>
        option.kind === kind &&
        option.deleted !== true &&
        answersToName(option, name),
    );
    const [first] = answering;
    if (!first) {
      problems.push({
        code: ExternalConfiguratorExportProblemCode.BaseOptionMissing,
        kind,
        name,
      });
    } else if (!answering.some(isOffered)) {
      problems.push({
        code: ExternalConfiguratorExportProblemCode.BaseOptionNotAvailable,
        kind,
        name,
        optionId: first.id,
        optionName: first.name,
      });
    }
  }
  return problems;
}

/** The offered options a reference may point at, by id (lower case). */
interface OfferedOptions {
  byId: ReadonlyMap<string, ExternalExportOption>;
}

/**
 * The kinds the configurator resolves one reference field in, as
 * `hydrateVariableOptionPresetData` of `@emerge/lt-cases` does. A written
 * reference is always `{ id, name }`, so the configurator resolves every one
 * by id; its `board` exemption never applies to this file.
 */
function acceptedBy(
  field: string,
  category: string,
): (option: ExternalExportOption) => boolean {
  switch (field) {
    case "partSelection": {
      const kinds = PART_SELECTION_KINDS_BY_CATEGORY[category] ?? [];
      return (option) => kinds.includes(option.kind);
    }
    case "rollerA":
    case "rollerB":
      return (option) =>
        option.kind === ROLLER_KIND &&
        option.rollerType === CREEPER_ROLLER_TYPE;
    default:
      return (option) => option.kind === PADDING_KIND;
  }
}

const REFERENCE_FIELDS = [
  "partSelection",
  "rollerA",
  "rollerB",
  "areaPaddingType",
  "borderPaddingType",
] as const;

/**
 * Checks that every option an exported preset references is in the produced
 * `user-options.json`, under a kind the configurator resolves that field in.
 */
function checkPresetReferences(
  exported: ExportedPreset,
  presetName: string,
  offered: OfferedOptions,
  allById: ReadonlyMap<string, ExternalExportOption>,
): ExternalConfiguratorExportProblem[] {
  const problems: ExternalConfiguratorExportProblem[] = [];
  for (const field of REFERENCE_FIELDS) {
    const reference = exported.data[field];
    if (!isRecord(reference) || typeof reference.id !== "string") continue;
    const accepts = acceptedBy(field, exported.category);

    const id = reference.id.toLowerCase();
    const match = offered.byId.get(id);
    if (match && accepts(match)) continue;

    const known = allById.get(id);
    const preset = {
      presetId: exported.id,
      presetName,
      field,
    };
    if (known && known.deleted !== true && !isOffered(known)) {
      problems.push({
        code: ExternalConfiguratorExportProblemCode.PresetOptionNotAvailable,
        ...preset,
        optionId: known.id,
        optionName: known.name,
        optionKind: known.kind,
      });
    } else {
      problems.push({
        code: ExternalConfiguratorExportProblemCode.PresetOptionUnknown,
        ...preset,
        optionId: reference.id,
        optionName:
          typeof reference.name === "string" ? reference.name : undefined,
      });
    }
  }
  return problems;
}

const indexById = (
  options: readonly ExternalExportOption[],
): Map<string, ExternalExportOption> => {
  const byId = new Map<string, ExternalExportOption>();
  for (const option of options) {
    if (typeof option.id === "string")
      byId.set(option.id.toLowerCase(), option);
  }
  return byId;
};

/**
 * Builds the files the external configurator (the case configurator on the
 * Thomann website) reads, from one snapshot of the options, presets and
 * instruments: only what is flagged `availableExternally`.
 *
 * `user-options.json` and `presets.json` are refused together, with the list
 * of problems, when the pair would break the configurator: an offered preset
 * that uses an option not offered, or an option the configurator always asks
 * for that is not offered.
 */
export function buildExternalConfiguratorExport({
  options,
  presets,
  instruments,
  now = new Date(),
}: ExternalConfiguratorExportInput): ExternalConfiguratorExport {
  const live = options.filter((option) => option.deleted !== true);
  const offered = live.filter(isOffered);
  const userOptions = exportAllOptions(offered).map(toExternalOption);
  const offeredIndex: OfferedOptions = { byId: indexById(offered) };
  const allById = indexById(options);

  const problems = [...checkBaseOptions(live), ...checkDescriptionsEn(offered)];
  const exportedPresets: ExportedPreset[] = [];
  for (const preset of presets) {
    if (preset.availableExternally !== true || preset.deleted) continue;
    // One preset at a time, so that one that cannot be written does not hide
    // the reference problems of the others.
    if (trimmedText(preset.nameEn) === "") {
      problems.push({
        code: ExternalConfiguratorExportProblemCode.PresetNameEnMissing,
        presetId: preset.id,
        presetName: preset.name,
      });
    }
    let exported: ExportedPreset[];
    try {
      exported = buildPresetExport([preset], live, "english");
    } catch (error) {
      if (!(error instanceof PresetExportError)) throw error;
      for (const reason of error.problems) {
        problems.push({
          code: ExternalConfiguratorExportProblemCode.PresetNotExportable,
          presetId: preset.id,
          presetName: preset.name,
          reason,
        });
      }
      continue;
    }
    for (const entry of exported) {
      problems.push(
        ...checkPresetReferences(entry, preset.name, offeredIndex, allById),
      );
      exportedPresets.push(entry);
    }
  }

  const { file, skippedIds } = buildInstrumentExport(instruments, now);
  const ok = problems.length === 0;
  return {
    problems,
    userOptions: ok ? userOptions : null,
    presets: ok ? exportedPresets : null,
    instruments: file,
    skippedInstrumentIds: skippedIds,
  };
}
